import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { AlertTriangle } from "lucide-react";

/**
 * Inline live warning shown under a slug input when the slug is already taken
 * by another brand, CMS page, clothing item, or a reserved static route.
 *
 * Props:
 *  - slug: current value of the slug field (or live-derived from name/title)
 *  - kind: "brand" | "cms_page" | "clothing" — the record we're editing
 *  - ignoreId: optional id of the record being edited (so it doesn't collide with itself)
 *  - urlPrefix: optional URL prefix used when rendering the message ("" for CMS, "/brands/", "/clothing/")
 */
export function SlugConflictWarning({ slug, kind, ignoreId, urlPrefix = "" }) {
  const [debounced, setDebounced] = useState(slug);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(slug), 350);
    return () => clearTimeout(t);
  }, [slug]);

  const { data } = useQuery({
    enabled: Boolean(debounced && debounced.trim().length > 0),
    queryKey: ["slug-conflict", kind, debounced, ignoreId || ""],
    queryFn: async () => {
      const params = { slug: debounced, kind };
      if (ignoreId) params.ignore_id = ignoreId;
      return (await api.get("/admin/slug-conflict", { params })).data;
    },
    staleTime: 5000,
  });

  if (!debounced || !data?.conflict) return null;

  const labels = {
    reserved: "a reserved system path",
    brand: "another brand",
    cms_page: "another CMS page",
    clothing: "another clothing item",
  };
  const what = labels[data.taken_by] || "another resource";
  const name = data.name ? ` ("${data.name}")` : "";

  return (
    <div
      className="mt-1 flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1.5"
      data-testid="slug-conflict-warning"
    >
      <AlertTriangle className="w-4 h-4 mt-px shrink-0" />
      <div>
        URL <code className="font-mono">{urlPrefix}/{debounced}</code> is already taken by {what}{name}.
        Choose a different name or slug.
      </div>
    </div>
  );
}
